import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { AppDispatch, RootState } from '../index';
import { API_BASE_URL } from '../api/baseApi';
import { favoritesApi } from '../api/favoritesApi';
import * as authService from '@/services/authService';

const FAVORITES_KEY = '@salon_favorites';

interface FavoritesState {
  shopIds: string[];
  stylistIds: string[];
  isLoading: boolean;
}

const INITIAL_FAVORITES = { shopIds: [], stylistIds: [] };

type FavoritesThunkConfig = { state: RootState; dispatch: AppDispatch };

const initialState: FavoritesState = {
  shopIds: INITIAL_FAVORITES.shopIds,
  stylistIds: INITIAL_FAVORITES.stylistIds,
  isLoading: false,
};

async function getToken(stateToken: string | null): Promise<string | null> {
  if (stateToken) return stateToken;
  try {
    const session = await authService.loadSession();
    return session?.token ?? null;
  } catch {
    return null;
  }
}

export const loadFavorites = createAsyncThunk<
  { shopIds: string[]; stylistIds: string[] },
  void,
  FavoritesThunkConfig
>('favorites/loadFavorites', async (_, { dispatch, getState }) => {
  const token = await getToken(getState().auth.token);

  let localFavs: { shopIds: string[]; stylistIds: string[] } | null = null;
  try {
    const raw = await AsyncStorage.getItem(FAVORITES_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      localFavs = {
        shopIds: Array.isArray(parsed?.shopIds) ? parsed.shopIds : [],
        stylistIds: Array.isArray(parsed?.stylistIds) ? parsed.stylistIds : [],
      };
    } else {
      localFavs = INITIAL_FAVORITES;
      await AsyncStorage.setItem(FAVORITES_KEY, JSON.stringify(INITIAL_FAVORITES));
    }
  } catch (err) {
    console.error('Failed to read favorites from AsyncStorage', err);
  }

  if (API_BASE_URL && token) {
    try {
      // GET /favorites/me is the one list the server has. This used to call /favorites/shops and
      // /favorites/stylists, which don't exist, so the saved favorites never loaded and the app
      // silently fell back to what this phone had stored (nothing on a new phone or reinstall).
      const all = await dispatch(
        favoritesApi.endpoints.getFavorites.initiate(undefined, { forceRefetch: true, subscribe: false }),
      ).unwrap();
      const ofType = (type: 'shop' | 'stylist') =>
        all.filter((f) => f.targetType.toLowerCase() === type).map((f) => f.targetId);
      const shopIds = ofType('shop');
      const stylistIds = ofType('stylist');
      await AsyncStorage.setItem(FAVORITES_KEY, JSON.stringify({ shopIds, stylistIds }));
      return { shopIds, stylistIds };
    } catch (err) {
      console.warn('Backend favorites load failed, falling back to local storage', err);
    }
  }

  return localFavs ?? { shopIds: [], stylistIds: [] };
});

export const toggleFavoriteShop = createAsyncThunk<string[], string, FavoritesThunkConfig>(
  'favorites/toggleFavoriteShop',
  async (shopId: string, { dispatch, getState }) => {
    const currentShopIds = getState().favorites.shopIds;
    const isCurrentlyFav = currentShopIds.includes(shopId);
    const nextShopIds = isCurrentlyFav
      ? currentShopIds.filter((id) => id !== shopId)
      : [...currentShopIds, shopId];

    const currentStylistIds = getState().favorites.stylistIds;
    dispatch(setFavorites({ shopIds: nextShopIds, stylistIds: currentStylistIds }));
    try {
      await AsyncStorage.setItem(
        FAVORITES_KEY,
        JSON.stringify({ shopIds: nextShopIds, stylistIds: currentStylistIds })
      );
    } catch (err) {
      console.error('Failed to persist favorite shop locally', err);
    }

    const token = await getToken(getState().auth.token);
    if (API_BASE_URL && token) {
      try {
        if (isCurrentlyFav) {
          await dispatch(favoritesApi.endpoints.removeFavorite.initiate({ targetType: 'shop', targetId: shopId })).unwrap();
        } else {
          await dispatch(favoritesApi.endpoints.addFavorite.initiate({ targetType: 'shop', targetId: shopId })).unwrap();
        }
      } catch (err) {
        console.warn('Backend sync for favorite shop failed', err);
      }
    }

    return nextShopIds;
  }
);

export const toggleFavoriteStylist = createAsyncThunk<string[], string, FavoritesThunkConfig>(
  'favorites/toggleFavoriteStylist',
  async (stylistId: string, { dispatch, getState }) => {
    const currentStylistIds = getState().favorites.stylistIds;
    const isCurrentlyFav = currentStylistIds.includes(stylistId);
    const nextStylistIds = isCurrentlyFav
      ? currentStylistIds.filter((id) => id !== stylistId)
      : [...currentStylistIds, stylistId];

    const currentShopIds = getState().favorites.shopIds;
    dispatch(setFavorites({ shopIds: currentShopIds, stylistIds: nextStylistIds }));
    try {
      await AsyncStorage.setItem(
        FAVORITES_KEY,
        JSON.stringify({ shopIds: currentShopIds, stylistIds: nextStylistIds })
      );
    } catch (err) {
      console.error('Failed to persist favorite stylist locally', err);
    }

    const token = await getToken(getState().auth.token);
    if (API_BASE_URL && token) {
      try {
        if (isCurrentlyFav) {
          await dispatch(favoritesApi.endpoints.removeFavorite.initiate({ targetType: 'stylist', targetId: stylistId })).unwrap();
        } else {
          await dispatch(favoritesApi.endpoints.addFavorite.initiate({ targetType: 'stylist', targetId: stylistId })).unwrap();
        }
      } catch (err) {
        console.warn('Backend sync for favorite stylist failed', err);
      }
    }

    return nextStylistIds;
  }
);

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    setFavorites(state, action: PayloadAction<{ shopIds: string[]; stylistIds: string[] }>) {
      state.shopIds = action.payload.shopIds;
      state.stylistIds = action.payload.stylistIds;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadFavorites.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loadFavorites.fulfilled, (state, action) => {
        state.shopIds = action.payload.shopIds;
        state.stylistIds = action.payload.stylistIds;
        state.isLoading = false;
      })
      .addCase(loadFavorites.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(toggleFavoriteShop.fulfilled, (state, action) => {
        state.shopIds = action.payload;
      })
      .addCase(toggleFavoriteStylist.fulfilled, (state, action) => {
        state.stylistIds = action.payload;
      });
  },
});

export const { setFavorites } = favoritesSlice.actions;
export default favoritesSlice.reducer;
